import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * SEO Component - Manage dynamic meta tags for each page
 * 
 * @param {string} title - Page title (will be appended with site name)
 * @param {string} description - Page description for search engines
 * @param {string} keywords - SEO keywords (optional)
 * @param {string} image - Open Graph image URL (optional)
 * @param {string} type - Open Graph type (default: 'website')
 * @param {boolean} noindex - Prevent indexing (default: false)
 * @param {Array} structuredData - Additional JSON-LD structured data objects
 */
const SEO = ({ 
  title, 
  description, 
  keywords = '',
  image = 'https://traineracademy.xyz/android-chrome-512x512.png',
  type = 'website',
  noindex = false,
  structuredData = []
}) => {
  const location = useLocation();
  const siteUrl = 'https://traineracademy.xyz';
  
  // Remove query strings for canonical URL to avoid duplicate content issues
  const canonicalUrl = `${siteUrl}${location.pathname}`;
  
  // Default fallback values
  const defaultTitle = 'Trainer Academy — Pokémon VGC Rankings, Battle Analysis & Competitive Stats';
  const defaultDescription = 'Trainer Academy is the competitive Pokémon VGC analytics platform. Explore usage rankings, analyze battle replays, and master VGC with data-driven insights.';
  
  const fullTitle = title ? `${title} | Trainer Academy` : defaultTitle;
  const metaDescription = description || defaultDescription;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Trainer Academy" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />

      {/* Additional structured data */}
      {structuredData.map((data, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
